// ReceiptReviewPage — the buyer confirms their own receipt. Image (or PDF)
// side-by-side with the AI-parsed fields; header-first, full line items
// editable; one-tap Submit (gated when the totals don't reconcile). Polls while
// digesting, then flashes "Detection complete". Routed by the public_id (UUID).

import BackLink from '../components/BackLink';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { formatTimestamp, formatEventDate } from '../utils/date-utils';
import useReceiptsStore from '../stores/useReceiptsStore';
import useUserStore from '../stores/useUserStore';
import { Receipt, ReceiptLineItem, EventOption, ReceiptHistoryEntry, ATTENTION_LABELS } from './types';
import StatusPill from './StatusPill';
import { formatCardLabel } from './cardLabel';
import HandOffModal from './HandOffModal';
import { prepareForUpload } from './prepareUpload';
import EventPicker from './EventPicker';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Checkbox } from '../components/ui/Checkbox';
import { useFilePicker } from '../components/ui/FilePick';
import { IconClose } from '../components/ui/icons';
import { Alert } from '../components/ui/Alert';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import ReceiptRequestField from '../Requisitions/ReceiptRequestField';

// The card picker's last entry: not a card, the door to the hand-off dialog.
const SOMEONE_ELSES_CARD = '__someone_else__';

// Only ReceiptRequestField still draws its input from this.
const inputClass =
  'w-full bg-gray-900/70 text-white text-sm px-3 py-1.5 rounded-md border border-gray-700/60 ' +
  'transition focus:outline-none focus:border-primary/70 focus:ring-2 focus:ring-primary/30 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';
// Money reads right-aligned + monospaced so columns line up (matches the app's ledger style).
const moneyClass = 'text-right font-mono tabular-nums';

// Strip $, commas, and whitespace so a pasted "$1,250.00" parses/persists as "1250.00".
const stripMoney = (v: string) => v.replace(/[$,\s]/g, '');
const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

const blankLine = (): ReceiptLineItem => ({ description: '', quantity: null, unit_price: null, amount: null });

// ── Edit history ─────────────────────────────────────────────────────────────
const HISTORY_MONEY_FIELDS = new Set(['subtotal', 'tax', 'tip', 'total']);
const HISTORY_FIELD_LABELS: Record<string, string> = {
  vendor_name: 'Vendor', vendor_address: 'Address', receipt_date: 'Date',
  subtotal: 'Subtotal', tax: 'Tax', tip: 'Tip', total: 'Total',
  category_id: 'Category', card_id: 'Card', event_id: 'Event', notes: 'Note',
  purchase_class_id: 'Bought for', requisition: 'Request',
};
// Status transitions read as a verb, not a raw enum value.
const HISTORY_STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted', approved: 'Approved', exported: 'Exported', synced: 'Synced',
  void: 'Voided', needs_review: 'Sent back for review', processing_failed: 'Detection failed',
};
const historyValue = (field: string, v: unknown) => {
  if (v == null || v === '') return '—';
  return HISTORY_MONEY_FIELDS.has(field) ? money(v) : String(v);
};
function historyRowText(e: ReceiptHistoryEntry): string {
  if (e.field === 'line_items') return 'Line items changed';
  if (e.field === 'status') return HISTORY_STATUS_LABELS[String(e.new_value)] || String(e.new_value);
  // A rescan hint has no before/after — it's the note a human handed the reader, kept
  // so a value that moved BECAUSE someone said something is distinguishable from one
  // the reader found on its own.
  if (e.field === 'rescan_hint') return `Asked the reader to look again: "${String(e.new_value)}"`;
  const label = HISTORY_FIELD_LABELS[e.field] || e.field;
  return `${label}: ${historyValue(e.field, e.old_value)} → ${historyValue(e.field, e.new_value)}`;
}

// Rotating-chevron collapsible trigger (matches ReconcilePage / ChargerInconsistencies).
function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      className={`flex-shrink-0 text-on-surface-variant transition-transform ${open ? 'rotate-90' : ''}`}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default function ReceiptReviewPage() {
  const { pid } = useParams<{ pid: string }>();
  const history = useHistory();
  const toast = useToast();
  const confirm = useConfirm();
  const { getReceipt, patchReceipt, replaceLineItems, submitReceipt, reassignReceipt, replacePhoto,
    rescanReceipt, deleteReceipt, dismissDuplicate, ensureFormOptions, formOptions,
    reviewFlow, advanceFlow, exitFlow, syncFlowIndex } =
    useReceiptsStore();
  const canFinancial = useUserStore((s: any) => !!s.user?.can_financial);
  const currentUserId = useUserStore((s: any) => s.user?.id);
  // Whether this receipt sits in the viewer's own queue. A financial user can open and
  // re-route anyone's receipt; the hand-off copy has to stop saying "my" and "yours" then.
  const isMine = (r: Receipt | null) => !!r && currentUserId === (r.owner_user_id || r.uploaded_by);

  // Review-all flow chrome shows only when THIS receipt is part of the active
  // deck. A deep link to some other receipt leaves the flow untouched but chrome-less
  // (returning to a deck pid resumes it). flowPos derives from the routed pid so
  // the "N of M" is always right; advanceFlow drives the store index in lockstep.
  const flowPos = reviewFlow ? reviewFlow.ids.indexOf(pid) : -1;
  const inFlow = flowPos >= 0;
  const flowTotal = reviewFlow?.ids.length ?? 0;

  // Advance to the next deck receipt, or bail to the queue at the end (its
  // "You're all caught up" empty state is the finale).
  const goNext = useCallback(() => {
    const next = advanceFlow();
    history.push(next ? `/receipts/${next}` : '/receipts');
  }, [advanceFlow, history]);

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justDetected, setJustDetected] = useState(false);
  const [ackMath, setAckMath] = useState(false); // unlocks submit when totals legitimately don't reconcile
  const [users, setUsers] = useState<any[]>([]);   // for the manual "send to someone" hand-off
  const [usersFailed, setUsersFailed] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false); // an empty list is an answer, not "still loading"
  const [pickRecipient, setPickRecipient] = useState(false);
  const [imgVersion, setImgVersion] = useState(0); // bumped on retake to refetch the preview
  const [rescanOpen, setRescanOpen] = useState(false); // inline "anything it missed?" panel
  const [rescanHint, setRescanHint] = useState('');
  // Edit history — collapsed by default, lazy-fetched on first expand.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [editHistory, setEditHistory] = useState<ReceiptHistoryEntry[]>([]);
  const initialized = useRef(false);
  const wasProcessing = useRef(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // editable form state
  const [vendor, setVendor] = useState('');
  const [address, setAddress] = useState('');
  const [date, setDate] = useState('');
  const [subtotal, setSubtotal] = useState('');
  const [tax, setTax] = useState('');
  const [tip, setTip] = useState('');
  const [total, setTotal] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryDeferred, setCategoryDeferred] = useState(false); // buyer chose "Not sure"
  const [classId, setClassId] = useState('');                     // purchase class (QBO Class)
  const [classDeferred, setClassDeferred] = useState(false);       // scanner chose "Not sure" on class
  const [cardId, setCardId] = useState('');
  const [eventId, setEventId] = useState('');
  const [eventName, setEventName] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiptLineItem[]>([]);

  const hydrate = (r: Receipt) => {
    setVendor(r.vendor_name || '');
    setAddress(r.vendor_address || '');
    setDate(r.receipt_date || '');
    setSubtotal(r.subtotal != null ? String(r.subtotal) : '');
    setTax(r.tax != null ? String(r.tax) : '');
    setTip(r.tip != null ? String(r.tip) : '');
    setTotal(r.total != null ? String(r.total) : '');
    setCategoryId(r.category_id != null ? String(r.category_id) : '');
    setCategoryDeferred(!!r.category_deferred);
    setClassId(r.purchase_class_id != null ? String(r.purchase_class_id) : '');
    setClassDeferred(!!r.class_deferred);
    setCardId(r.card_id != null ? String(r.card_id) : '');
    setEventId(r.event_id || '');
    setEventName(r.event_name || null);
    setNotes(r.notes || '');
    setLines((r.line_items || []).map((li) => ({ ...li })));
  };

  const load = useCallback(async () => {
    const r: Receipt = await getReceipt(pid);
    setReceipt(r);
    const ready = r.status !== 'processing' && r.status !== 'uploaded';
    if (!initialized.current && ready) { initialized.current = true; hydrate(r); }
    // Flash "Detection complete" the moment it flips from processing → ready.
    if (wasProcessing.current && ready) {
      setJustDetected(true);
      if (detectTimer.current) clearTimeout(detectTimer.current);
      detectTimer.current = setTimeout(() => setJustDetected(false), 2500);
    }
    wasProcessing.current = !ready;
    if (!ready) pollTimer.current = setTimeout(load, 2500); // keep polling while the digest runs
  }, [pid, getReceipt]);

  useEffect(() => {
    if (inFlow) syncFlowIndex(pid); // realign the deck index to whatever pid actually mounted
    ensureFormOptions(pid).catch(() => {});
    load().catch((e) => {
      // A deck receipt that 404s (voided / routed away mid-flow) auto-advances
      // silently rather than dead-ending on an error toast.
      if (inFlow) { goNext(); return; }
      toast.error(e?.message || 'Could not load receipt');
    });
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      if (detectTimer.current) clearTimeout(detectTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Authed image/PDF blob (the proxy needs the Bearer header — a plain src can't send it).
  useEffect(() => {
    let revoked = false;
    let url: string | null = null;
    (async () => {
      try {
        const resp = (await apiFetchAuthed(`/receipts/${pid}/image`, { skipJsonParse: true, silent: true })) as Response;
        if (!resp.ok) return;
        const blob = await resp.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch { /* image optional */ }
    })();
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [pid, imgVersion]);

  // Lazily load the user list only when the buyer opens the manual hand-off picker.
  // A failure is recorded, not swallowed: the picker is the whole dialog, so an
  // empty one with nothing to say is a dead end.
  useEffect(() => {
    if (pickRecipient && !usersLoaded) {
      setUsersFailed(false);
      apiFetchAuthed('/users/active', { silent: true })
        .then((u: any) => { setUsers(u || []); setUsersLoaded(true); })
        .catch(() => setUsersFailed(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickRecipient]);

  const setLine = (i: number, patch: Partial<ReceiptLineItem>) =>
    setLines((prev) => prev.map((li, idx) => (idx === i ? { ...li, ...patch } : li)));

  // Live math check, mirroring the backend math_mismatch rule: subtotal + tax +
  // tip = total. Gated on BOTH subtotal and total being present — a receipt that
  // prints only a total (no subtotal) has nothing to reconcile, so don't nag.
  // Recomputed as the buyer types so the warning + submit gate clear the instant
  // they fix the numbers.
  const mathDelta = useMemo(() => {
    const t = parseFloat(stripMoney(total));
    const s = parseFloat(stripMoney(subtotal));
    if (!Number.isFinite(t) || !Number.isFinite(s)) return null;
    const x = parseFloat(stripMoney(tax));
    const tp = parseFloat(stripMoney(tip));
    const sum = s + (Number.isFinite(x) ? x : 0) + (Number.isFinite(tp) ? tp : 0);
    return Math.round((t - sum + Number.EPSILON) * 100) / 100;
  }, [subtotal, tax, tip, total]);
  const mathOff = mathDelta != null && Math.abs(mathDelta) > Math.max(0.05, Math.abs(parseFloat(stripMoney(total)) || 0) * 0.02);

  // Live line-item sum check, mirroring the backend's line_items_dont_sum rule the
  // same way mathDelta above mirrors math_mismatch. Without it the two warning boxes
  // disagree about WHEN they are true: the red math one recomputes as the buyer types,
  // while a server-snapshot yellow one keeps reporting a discrepancy they have already
  // fixed in the form. A warning that cannot clear teaches people to ignore it.
  const lineItemsOff = useMemo(() => {
    if (!lines.length) return false;
    const sum = lines.reduce((a, li) => a + (parseFloat(stripMoney(String(li.amount ?? ''))) || 0), 0);
    const s = parseFloat(stripMoney(subtotal));
    const t = parseFloat(stripMoney(total));
    const cmp = Number.isFinite(s) ? s : t; // subtotal is the yardstick, total only if there is none
    if (!Number.isFinite(cmp)) return false;
    return Math.abs(sum - cmp) > Math.max(0.05, Math.abs(cmp) * 0.02);
  }, [lines, subtotal, total]);

  // Re-arm the math acknowledgment whenever the numbers change, so a stale
  // "I've checked" can't authorize a different (still-off) set of totals.
  useEffect(() => { setAckMath(false); }, [subtotal, tax, tip, total]);

  // Unsaved-edit check, used ONLY to decide whether AI Rescan needs to warn first.
  // Scoped to the fields a fresh digest overwrites; event, notes and purchase class
  // survive a re-read untouched, so they are not at risk and would only cause a
  // false alarm. Line items are left out deliberately — they are overwritten too,
  // but the money/vendor fields move whenever someone is mid-correction, which makes
  // them a reliable enough proxy without diffing an array on every keystroke.
  const dirty = useMemo(() => {
    if (!receipt) return false;
    const s = (v: any) => (v == null ? '' : String(v));
    // Money compares by VALUE, not by text. Postgres numeric arrives as a string
    // ("12.00" — server.js only overrides the DATE parser, and node-postgres keeps
    // numeric as text for precision), so a field the buyer typed as "12.5" and saved
    // reads back as "12.50" and a string compare calls it dirty forever after. That
    // false alarm matters: a confirm dialog that cries wolf teaches people to click
    // through the one time it is real.
    const sameMoney = (typed: string, stored: any) => {
      const a = parseFloat(stripMoney(typed));
      const b = stored == null ? NaN : Number(stored);
      if (Number.isFinite(a) && Number.isFinite(b)) return Math.abs(a - b) < 0.005;
      return typed === s(stored); // blank vs blank, or something unparseable on either side
    };
    return vendor !== s(receipt.vendor_name)
      || address !== s(receipt.vendor_address)
      || date !== s(receipt.receipt_date)
      || !sameMoney(subtotal, receipt.subtotal)
      || !sameMoney(tax, receipt.tax)
      || !sameMoney(tip, receipt.tip)
      || !sameMoney(total, receipt.total);
  }, [receipt, vendor, address, date, subtotal, tax, tip, total]);

  const persist = async () => {
    await patchReceipt(pid, {
      vendor_name: vendor || null,
      vendor_address: address || null,
      receipt_date: date || null,
      subtotal: subtotal === '' ? null : (stripMoney(subtotal) as any),
      tax: tax === '' ? null : (stripMoney(tax) as any),
      tip: tip === '' ? null : (stripMoney(tip) as any),
      total: total === '' ? null : (stripMoney(total) as any),
      category_id: categoryDeferred || categoryId === '' ? null : (Number(categoryId) as any),
      category_deferred: categoryDeferred,
      purchase_class_id: classDeferred || classId === '' ? null : (Number(classId) as any),
      // No silent blank: an empty pick saves as "Not sure" (deferred), same as create().
      class_deferred: classDeferred || classId === '',
      card_id: cardId === '' ? null : (Number(cardId) as any),
      event_id: eventId || null,
      notes: notes.trim() || null,
    });
    await replaceLineItems(pid, lines);
  };

  // Linking a request can fill this receipt's class and show on the server.
  // Take those two into the form when it still has them blank, or the next save
  // would write the blanks back over them.
  const adoptRequestPrefill = async () => {
    try {
      const r: Receipt = await getReceipt(pid);
      setReceipt(r);
      if (classId === '' && !classDeferred && r.purchase_class_id != null) setClassId(String(r.purchase_class_id));
      if (!eventId && r.event_id) { setEventId(r.event_id); setEventName(r.event_name || null); }
    } catch { /* the link landed; the form keeps what it has */ }
  };

  const handleReviewLater = async () => {
    setSaving(true);
    try {
      await persist();
      // In flow, "Review later" is skip-with-save: it advances just like Skip.
      if (inFlow) { toast.success('Saved for later'); goNext(); }
      else { toast.success('Saved. Pick it back up anytime'); history.push('/receipts'); }
    } catch (e: any) { toast.error(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await persist();
      await submitReceipt(pid);
      if (inFlow) { toast.success('Submitted'); goNext(); }
      else { toast.success('Submitted. Thanks!'); history.push('/receipts'); }
    } catch (e: any) { toast.error(e?.message || 'Submit failed'); }
    finally { setSaving(false); }
  };

  // Retake: swap in a clearer photo and let the digest re-read from scratch. Reuses
  // the existing poll loop + "Detection complete" flash; re-arms hydrate so the
  // form fills from the NEW read, and bumps imgVersion so the preview refetches.
  const handleRetake = async (file: File) => {
    setSaving(true);
    try {
      const updated = await replacePhoto(pid, await prepareForUpload(file));
      initialized.current = false;
      wasProcessing.current = true;
      setImgVersion((v) => v + 1);
      setReceipt(updated); // status 'processing' → spinner shows immediately
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollTimer.current = setTimeout(load, 1200);
      toast.success('New photo uploaded, reading it again');
    } catch (e: any) { toast.error(e?.message || 'Could not replace the photo'); }
    finally { setSaving(false); }
  };

  const retake = useFilePicker({ accept: 'image/*,application/pdf', onFiles: (files) => { handleRetake(files[0]); } });

  // AI Rescan: the picture is fine, the READ was wrong. Shares every bit of retake's
  // plumbing (poll loop, hydrate re-arm, "Detection complete" flash) except the
  // imgVersion bump — the image did not change, so refetching the preview would only
  // flicker it. The optional hint is one line about what the reader missed; the API
  // hands it to the model as untrusted data, never as instructions.
  const runRescan = async (hint?: string) => {
    setSaving(true);
    try {
      const updated = await rescanReceipt(pid, hint);
      initialized.current = false;
      wasProcessing.current = true;
      setReceipt(updated); // status 'processing' → spinner shows immediately
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollTimer.current = setTimeout(load, 1200);
      setRescanOpen(false);
      setRescanHint('');
      toast.success(hint ? 'Reading it again with your note' : 'Reading it again');
    } catch (e: any) { toast.error(e?.message || 'Could not re-read this receipt'); }
    finally { setSaving(false); }
  };

  // A rescan replaces the header + line items, so unsaved typing would vanish with no
  // warning. Retake is exempt from this prompt only because a new photo makes the
  // replacement self-evident; here nothing on screen changes to explain it.
  const handleRescan = async (hint?: string) => {
    if (dirty) {
      const ok = await confirm({
        title: 'Read it again and replace your edits?',
        message: 'The reader will fill the fields in again from the same photo. Anything you typed but have not saved will be replaced.',
        confirmLabel: 'Read it again',
        tone: 'danger',
      });
      if (!ok) return;
    }
    await runRescan(hint);
  };

  // Hard delete — the row and the stored file both go. Safe to put in a buyer's hands
  // because the card statement is the independent record of the spend: a charge whose
  // receipt is gone comes back as a missing receipt at reconcile time. This discards a
  // bad scan; it cannot make a purchase disappear.
  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete this receipt?',
      message: 'This one is gone for good, along with anything you typed. If the purchase was on a company card, it comes back as a missing receipt when the statement is reconciled.',
      confirmLabel: 'Delete receipt',
      tone: 'danger',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deleteReceipt(pid);
      toast.success('Receipt deleted');
      if (inFlow) goNext(); else history.push('/receipts');
    } catch (e: any) { toast.error(e?.message || 'Could not delete this receipt'); }
    finally { setSaving(false); }
  };

  // Soft-dup flag: "it's a different purchase" clears it server-side; reload
  // through the existing load() path (not a manual setReceipt) so it doesn't
  // clobber whatever the buyer's already typed into the form.
  const handleDismissDuplicate = async () => {
    setSaving(true);
    try {
      await dismissDuplicate(pid);
      await load();
      toast.success('Got it, treating this as a different purchase');
    } catch (e: any) { toast.error(e?.message || 'Could not clear the duplicate flag'); }
    finally { setSaving(false); }
  };

  const handleReassign = async (toUserId: string, name: string) => {
    const ok = await confirm({
      title: 'Send for review',
      message: isMine(receipt)
        ? `Send this receipt to ${name} to review and file? It moves to their queue and leaves yours.`
        : `Send this receipt to ${name} to review and file? It moves to their queue.`,
      confirmLabel: `Send to ${name}`,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await reassignReceipt(pid, toUserId);
      toast.success(`Sent to ${name} for review`);
      if (inFlow) goNext(); else history.push('/receipts');
    } catch (e: any) { toast.error(e?.message || 'Could not hand it off'); }
    finally { setSaving(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const data: { entries: ReceiptHistoryEntry[] } = await apiFetchAuthed(`/receipts/${pid}/history`, { silent: true });
      setEditHistory(data?.entries || []);
      setHistoryLoaded(true);
    } catch { setHistoryError(true); }
    finally { setHistoryLoading(false); }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && !historyLoaded && !historyLoading) loadHistory();
  };

  if (!receipt) {
    return (
      <PageLayout kind="record" className="pt-4 lg:pt-6">
        <LogoSpinner label="Loading the receipt" />
      </PageLayout>
    );
  }

  const processing = receipt.status === 'processing' || receipt.status === 'uploaded';
  const failed = receipt.status === 'processing_failed';
  const submitted = receipt.status === 'submitted' || receipt.status === 'approved';
  // Dawn has reconciled it (or it's moved on to export/QBO sync) — read-only from
  // here. Backend already 409s edits at this stage; this just makes the UI honest.
  const locked = receipt.status === 'approved' || receipt.status === 'exported' || receipt.status === 'synced';
  // Someone else's card → this isn't the uploader's to file. Hand-off only, no submit.
  const isForeign = !!receipt.detected_card_owner_id && !submitted && !locked;
  // "Not my card": the manual hand-off, for when detection could not say whose card
  // it was. The two statuses are the server's HANDOFF_STATUSES, spelled out rather
  // than derived, so the button and the endpoint can never disagree — a derived rule
  // let it show on a voided receipt, which reassign refuses. A failed scan counts:
  // nobody can read it, and the person whose card it was is the one who can retake it.
  // The other door, when detection DID name a cardholder, is the callout above.
  const canHandOff = !isForeign && (receipt.status === 'needs_review' || failed);
  // The picker's roster, grouped by whose cards they are. One group renders flat;
  // a scanner reviewing a handed-off receipt sees the owner's cards named as such.
  // A financial user is offered the whole roster, so cards that belong to neither
  // the viewer nor the receipt's owner get their own heading rather than the owner's.
  const cardGroups = (() => {
    const cards = formOptions?.cards || [];
    const ownerId = receipt.owner_user_id || receipt.uploaded_by;
    const groups: { label: string; cards: typeof cards }[] = [
      { label: 'My cards', cards: cards.filter((c) => c.owner_user_id === currentUserId) },
      { label: 'Shared cards', cards: cards.filter((c) => c.owner_user_id == null) },
      { label: receipt.owner_name ? `${receipt.owner_name}'s cards` : 'Their cards',
        cards: cards.filter((c) => c.owner_user_id != null && c.owner_user_id !== currentUserId && c.owner_user_id === ownerId) },
      { label: 'Other cardholders',
        cards: cards.filter((c) => c.owner_user_id != null && c.owner_user_id !== currentUserId && c.owner_user_id !== ownerId) },
    ];
    return groups.filter((g) => g.cards.length > 0);
  })();
  // "Couldn't read it" — the digest either hard-failed or came back essentially
  // blank (too blurry to read). Either way, orient the buyer to fill it in by
  // hand and suppress the misleading "looks clean" note below.
  const unreadable = !processing && !submitted && !isForeign && !locked &&
    (failed || (!receipt.vendor_name && receipt.total == null));
  const isPdf = (receipt.mime_type || '').includes('pdf');
  // Category needs a manual pick when the reader flagged it AND none is chosen yet
  // (cleared live the moment the buyer picks a category — or chooses "Not sure",
  // which is a deliberate deferral, not an unfinished field).
  const needsCategory = !!receipt.attention_reasons?.includes('category_unconfirmed') && categoryId === '' && !categoryDeferred;
  const categoryGuessLabel = receipt.ai_guessed_category_key
    ? (formOptions?.categories.find((c) => c.key === receipt.ai_guessed_category_key)?.label || null)
    : null;
  // Softer, non-math flags come from the server snapshot; math + category are handled inline.
  // Softer flags from the server snapshot. math_mismatch, category and line-item sum
  // are all handled live instead, so the stale snapshot of each is dropped here and
  // line_items_dont_sum is re-added from the LIVE check — otherwise a buyer who
  // deletes a bogus line still stares at the warning until they save and reload.
  const otherReasons = [
    ...(lineItemsOff ? ['line_items_dont_sum'] : []),
    ...(receipt.attention_reasons || []).filter((r) =>
      r !== 'math_mismatch' && r !== 'category_unconfirmed' && r !== 'line_items_dont_sum'),
  ];
  // Required to submit (date intentionally optional — never block on a missing date).
  // Mirrors the backend REQUIRED_TO_SUBMIT; a blank field catches both "unread" and "unsure".
  const missingRequired = [
    !vendor.trim() && 'vendor', total === '' && 'total', (!categoryId && !categoryDeferred) && 'category', !cardId && 'card',
  ].filter((x): x is string => Boolean(x));
  const submitBlocked = (mathOff && !ackMath) || missingRequired.length > 0;

  return (
    <PageLayout kind="record" className="pt-4 lg:pt-6" header={(
      <>
      {inFlow && (
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 2xl:-mx-8 -mt-4 lg:-mt-6 mb-4 px-4 sm:px-6 2xl:px-8 py-2.5 bg-gray-900/95 backdrop-blur border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-on-surface-variant whitespace-nowrap">
              Reviewing <span className="text-white font-semibold tabular-nums">{flowPos + 1}</span> of <span className="tabular-nums">{flowTotal}</span>
            </span>
            <Progress className="flex-1" size="sm" label="Receipts reviewed" value={flowPos + 1} max={flowTotal} />
            <button onClick={goNext} disabled={saving}
                    className={buttonClass('quiet', 'sm')}>Skip</button>
            <button onClick={exitFlow}
                    className={buttonClass('quiet', 'sm')}>Exit</button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <BackLink to="/receipts" label="Receipts" onClick={() => { if (inFlow) exitFlow(); }} />
        {!processing && <StatusPill status={receipt.status} />}
      </div>
      </>
    )}>

      {/* One hidden picker for BOTH retake triggers — the "couldn't read it" banner
          and the always-present action row under the image. Lives at this level so
          neither trigger depends on the other's block being rendered. */}
      {retake.input}

      <Reveal show={processing}>
        <div className="text-on-surface-variant flex items-center gap-2.5 py-12 justify-center">
          <LogoSpinner size="inline" />
          Detecting your receipt… this usually takes a few seconds.
        </div>
      </Reveal>

      <Reveal show={!!unreadable}>
        <div className="pb-3">
        <Alert
          tone="warning"
          title="We couldn't read this receipt. Please fill in the details below."
          action={(
            <button
              onClick={() => retake.open()}
              disabled={saving}
              className={buttonClass('warn', 'md')}
            >
              Retake photo
            </button>
          )}
        >
          Type in what's printed on the receipt, or retake a clearer photo and we'll read it again.
        </Alert>
        </div>
      </Reveal>

      <Reveal show={!processing && !!receipt.likely_duplicate}>
        {receipt.likely_duplicate && (
        <div className="pb-3">
        <Alert
          tone="warning"
          title="This looks like a duplicate of another receipt you loaded."
          action={(
            <>
              <Link to={`/receipts/${receipt.likely_duplicate.public_id}`} className={buttonClass('tonal', 'md')}>
                View it
              </Link>
              <button onClick={handleDismissDuplicate} disabled={saving} className={buttonClass('quiet', 'md')}>
                It's a different purchase
              </button>
            </>
          )}
        >
          <Panel label="The receipt it matches" on="panel" padding="sm" className="mt-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-on-surface truncate">{receipt.likely_duplicate.vendor_name || 'Unknown vendor'}</span>
              <StatusPill status={receipt.likely_duplicate.status} className="shrink-0" />
            </div>
            <div className="text-on-surface-variant text-xs mt-0.5">
              {receipt.likely_duplicate.receipt_date ? formatEventDate(receipt.likely_duplicate.receipt_date) : '—'} · <span className="font-mono tabular-nums">{money(receipt.likely_duplicate.total)}</span>
            </div>
          </Panel>
        </Alert>
        </div>
        )}
      </Reveal>

      {!processing && (
        <FadeIn>
        <div className="grid md:grid-cols-2 2xl:grid-cols-[minmax(0,1fr)_minmax(0,48rem)] gap-5">
          {/* Image / PDF, with the two "read it again" actions directly beneath it —
              they act on the scan, not on the form, so they belong to this column. */}
          <div>
            <Panel label="Receipt image" padding="sm" className="min-h-[260px] items-center justify-center">
              {imageUrl ? (
                isPdf ? (
                  <>
                    <iframe src={imageUrl} title="Receipt PDF" className="w-full h-[70vh] rounded bg-white" />
                    <a href={imageUrl} target="_blank" rel="noreferrer" className="text-xs text-on-surface-variant hover:text-white mt-2">Open in new tab</a>
                  </>
                ) : (
                  <a href={imageUrl} target="_blank" rel="noreferrer">
                    <img src={imageUrl} alt="Receipt" className="max-h-[70vh] max-w-full rounded" />
                  </a>
                )
              ) : (
                <EmptyState variant="area">No image</EmptyState>
              )}
            </Panel>

            {/* Rescan is same-photo, retake is new-photo — two different fixes for two
                different problems, so they read as a pair rather than one hidden
                inside the other. Both are pointless once the receipt is out of the
                buyer's hands (the backend 409s), so the pair hides with it. */}
            <Reveal show={!submitted && !locked && !isForeign}>
              <div className="pt-2">
              <FadeIn key={rescanOpen ? 'open' : 'closed'}>
              {rescanOpen ? (
                <Panel label="Read it again" padding="sm" className="gap-2">
                  <label className="block text-xs text-on-surface-variant" htmlFor="rescan-hint">
                    Anything it missed? <span className="text-on-surface-variant">(optional)</span>
                  </label>
                  <Field
                    id="rescan-hint"
                    autoFocus
                    value={rescanHint}
                    maxLength={280}
                    onChange={(e) => setRescanHint(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRescan(rescanHint.trim() || undefined); } }}
                    placeholder="e.g. the tip was added by hand on top of the printed one"
                  />
                  <p className="text-[11px] text-on-surface-variant">
                    A pointer for where to look. The reader still takes its numbers off the receipt, not from this note.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleRescan(rescanHint.trim() || undefined)}
                      disabled={saving}
                      className={buttonClass('filled', 'md')}
                    >
                      {saving ? 'Reading…' : 'Read it again'}
                    </button>
                    <button
                      onClick={() => { setRescanOpen(false); setRescanHint(''); }}
                      className={buttonClass('quiet', 'md')}
                    >
                      cancel
                    </button>
                  </div>
                </Panel>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex flex-1 [&>span]:flex [&>span]:w-full"><Tooltip label="Re-read the same photo. Use this when the picture is fine but the numbers came out wrong">
                    <button
                      onClick={() => setRescanOpen(true)}
                      disabled={saving}
                      className={`${buttonClass('quiet', 'md')} flex-1`}
                    >
                      AI Rescan
                    </button>
                  </Tooltip></div>
                  <div className="flex flex-1 [&>span]:flex [&>span]:w-full"><Tooltip label="Swap in a clearer photo. Use this when the picture itself is the problem">
                    <button
                      onClick={() => retake.open()}
                      disabled={saving}
                      className={`${buttonClass('quiet', 'md')} flex-1`}
                    >
                      Retake photo
                    </button>
                  </Tooltip></div>
                </div>
              )}
              </FadeIn>
              </div>
            </Reveal>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            {/* The alerts keep their gap as padding inside each Reveal, and the group's -mb-3
                cancels the column's space-y margin on the next sibling, so a closing alert
                does not leave a 12px hop behind. */}
            <div className="-mb-3">
            <Reveal show={justDetected}>
              <div className="pb-3"><Alert tone="success" variant="note">Detection complete. Give it a quick look.</Alert></div>
            </Reveal>

            <Reveal show={locked}>
              <div className="pb-3">
              <Alert tone="info" variant="note" live={false}>
                Approved and locked.{receipt.approved_at ? ` ${formatTimestamp(receipt.approved_at)}` : ''}
              </Alert>
              </div>
            </Reveal>

            <Reveal show={isForeign}>
              <div className="pb-3">
              <Alert
                tone="info"
                title={`This was paid on ${receipt.detected_card_owner_name || 'someone else'}'s card. It's theirs to file.`}
                action={(
                  <button
                    onClick={() => handleReassign(receipt.detected_card_owner_id!, receipt.detected_card_owner_name || 'them')}
                    disabled={saving}
                    className={buttonClass('info', 'md')}
                  >
                    Send to {receipt.detected_card_owner_name || 'them'} for review
                  </button>
                )}
              >
                Send it over and it lands in their review queue. You won't submit this one yourself.
              </Alert>
              </div>
            </Reveal>

            <Reveal show={mathOff}>
              <div className="pb-3">
              <Alert tone="error" title={`These numbers don't add up${mathDelta != null ? `, off by $${Math.abs(mathDelta).toFixed(2)}` : ''}.`}>
                <p className="m-0">
                  Check the subtotal, tax, tip, and total against the receipt. If a tip was added by hand, enter it in the Tip field.
                </p>
                <Checkbox className="mt-2" disabled={locked} checked={ackMath} onChange={(e) => setAckMath(e.target.checked)}
                  label="I've checked these numbers, the total is right." />
              </Alert>
              </div>
            </Reveal>

            <Reveal show={otherReasons.length > 0}>
              <div className="pb-3">
              <Alert tone="warning" title="Worth a quick look:">
                <ul className="m-0 list-disc pl-5">
                  {otherReasons.map((r) => <li key={r}>{ATTENTION_LABELS[r] || r}</li>)}
                </ul>
              </Alert>
              </div>
            </Reveal>

            <Reveal show={!mathOff && otherReasons.length === 0 && total !== ''}>
              <div className="pb-3"><Alert tone="success" variant="text" className="text-xs">Numbers reconcile. Looks clean.</Alert></div>
            </Reveal>
            </div>

            {receipt.ai_remarks && (
              <Panel title="Reader's note" level={3} padding="sm" className="text-sm text-on-surface">
                {receipt.ai_remarks}
              </Panel>
            )}

            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Vendor"><Field warn={!vendor.trim()} value={vendor} onChange={(e) => setVendor(e.target.value)} disabled={locked} /></LabeledField>
              <LabeledField label="Date"><Field type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={locked} /></LabeledField>
              <LabeledField label="Subtotal"><Field className={moneyClass} value={subtotal} onChange={(e) => setSubtotal(e.target.value)} inputMode="decimal" disabled={locked} /></LabeledField>
              <LabeledField label="Tax"><Field className={moneyClass} value={tax} onChange={(e) => setTax(e.target.value)} inputMode="decimal" disabled={locked} /></LabeledField>
              <LabeledField label="Tip"><Field className={moneyClass} value={tip} onChange={(e) => setTip(e.target.value)} inputMode="decimal" placeholder="0.00" disabled={locked} /></LabeledField>
              <LabeledField label="Total"><Field warn={total === ''} className={moneyClass} value={total} onChange={(e) => setTotal(e.target.value)} inputMode="decimal" disabled={locked} /></LabeledField>
              <LabeledField label="Category">
                {/* '__notsure__' is a local sentinel for the "Not sure" choice — it maps
                    to category_deferred (category stays empty) on save, not to a real category. */}
                <div className={`rounded-control ${categoryId || categoryDeferred ? '' : 'ring-1 ring-amber-500/50'}`}>
                  <Select
                    label="Category"
                    disabled={locked}
                    value={categoryDeferred ? '__notsure__' : categoryId}
                    onChange={(v) => {
                      setCategoryDeferred(v === '__notsure__');
                      setCategoryId(v === '__notsure__' ? '' : v);
                    }}
                    options={[
                      { key: '', label: needsCategory ? 'Choose…' : '—' },
                      { key: '__notsure__', label: 'Not sure (let the bookkeeper decide)' },
                      // financial_only categories (e.g. Meals & Entertainment) are the
                      // bookkeeper's to assign at reconcile: hidden from the buyer's
                      // picker, but still listed when already assigned so a locked
                      // receipt displays its true category.
                      ...[...(formOptions?.categories || [])]
                        .filter((c) => !c.financial_only || String(c.id) === categoryId)
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((c) => ({ key: String(c.id), label: c.label })),
                    ]}
                  />
                </div>
                <Reveal show={!!needsCategory}>
                  <span className="block text-[10px] text-amber-400/90 pt-1">
                    Reader wasn't sure{categoryGuessLabel ? `, guessed ${categoryGuessLabel}` : ''}. Pick one, or choose "Not sure".
                  </span>
                </Reveal>
                <Reveal show={categoryDeferred}>
                  <span className="block text-[10px] text-on-surface-variant pt-1">Marked "Not sure". The bookkeeper sets the category during reconciliation.</span>
                </Reveal>
                <Reveal show={!categoryDeferred && receipt.category_source === 'memory'}>
                  <span className="block text-[10px] text-on-surface-variant pt-1">Filled from your history with this vendor.</span>
                </Reveal>
              </LabeledField>
              <LabeledField label="Bought for">
                {/* Purchase class (QBO Class). '__notsure__' is a local sentinel that maps
                    to class_deferred (class left empty) on save, like the category picker.
                    Deliberately NO blank option: a class is never a silent blank (create()'s
                    rule) — an unset/legacy-blank state displays and saves as "Not sure". */}
                <Select
                  label="Bought for"
                  disabled={locked}
                  value={classDeferred || classId === '' ? '__notsure__' : classId}
                  onChange={(v) => {
                    setClassDeferred(v === '__notsure__');
                    setClassId(v === '__notsure__' ? '' : v);
                  }}
                  options={[
                    { key: '__notsure__', label: 'Not sure (let the bookkeeper decide)' },
                    ...(formOptions?.classes || []).map((c) => ({ key: String(c.id), label: c.label })),
                  ]}
                />
                <Reveal show={classDeferred}>
                  <span className="block text-[10px] text-on-surface-variant pt-1">Marked "Not sure". The bookkeeper sets the class during reconciliation.</span>
                </Reveal>
              </LabeledField>
              <LabeledField label="Card">
                {/* The roster is this person's own cards plus the shared ones, never the
                    company's. A receipt paid with somebody else's card leaves through the
                    last entry, which is the hand-off: the picker is where people look
                    for the card, so that is where the way out lives. */}
                <div className={`rounded-control ${cardId ? '' : 'ring-1 ring-amber-500/50'}`}>
                  <Select
                    label="Card"
                    disabled={locked}
                    value={cardId}
                    onChange={setCardId}
                    options={[
                      { key: '', label: '—' },
                      // The receipt's own card stays selectable even when it is outside the
                      // viewer's roster: a scanner reviewing a handed-off receipt would
                      // otherwise see a blank card and be unable to submit.
                      ...(receipt.card_id != null && !formOptions?.cards.some((c) => c.id === receipt.card_id)
                        ? [{ key: String(receipt.card_id), label: formatCardLabel({ label: receipt.card_label || '', last4: receipt.card_last4 || null }) }]
                        : []),
                      ...cardGroups.flatMap((g) => g.cards.map((c) => ({
                        key: String(c.id),
                        label: formatCardLabel(c),
                        group: cardGroups.length === 1 ? undefined : g.label,
                      }))),
                    ]}
                    actions={canHandOff ? [{ key: SOMEONE_ELSES_CARD, label: "Someone else's card…", onSelect: () => setPickRecipient(true) }] : undefined}
                  />
                </div>
                <Reveal show={!!formOptions && formOptions.cards.length === 0 && canHandOff}>
                  <span className="block text-[10px] text-on-surface-variant pt-1">No card is assigned to you. Pick the cardholder and this receipt goes to them.</span>
                </Reveal>
              </LabeledField>
            </div>

            <LabeledField label="Address"><Field value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city, state" disabled={locked} /></LabeledField>

            <LabeledField label="Event / show (optional)">
              <EventPicker
                value={eventId}
                valueName={eventName}
                date={date}
                onSelect={(e: EventOption) => { setEventId(e.id); setEventName(e.name); }}
                onClear={() => { setEventId(''); setEventName(null); }}
                disabled={locked}
              />
              <Reveal show={!!eventId && eventId === receipt.ai_guessed_event_id}>
                <span className="block text-[10px] text-on-surface-variant pt-1">Auto-tagged from your crew shift. Change if it's wrong.</span>
              </Reveal>
            </LabeledField>

            <ReceiptRequestField
              receiptPid={pid}
              labelClassName="block text-xs text-on-surface-variant mb-1"
              fieldClassName={inputClass}
              onLinked={adoptRequestPrefill}
            />

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-2 text-xs font-medium text-on-surface-variant">
                  <span className="w-1 h-3.5 rounded-full bg-primary/60" />Line items
                </span>
                {!locked && <button onClick={() => setLines((p) => [...p, blankLine()])} className={buttonClass('quiet', 'sm')}>+ add</button>}
              </div>
              <Reveal show={lines.length > 0}>
                <div className="grid grid-cols-[minmax(0,1fr)_3.25rem_6rem_1.75rem] gap-1.5 px-1 pb-1 text-[10px] uppercase tracking-wider text-on-surface-variant">
                  <span>Item</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Amount</span>
                  <span />
                </div>
              </Reveal>
              <div className="space-y-1.5">
                {lines.map((li, i) => {
                  const q = li.quantity != null && li.quantity !== '' ? Number(li.quantity) : null;
                  const amt = li.amount != null && li.amount !== '' ? Number(li.amount) : null;
                  const showEa = q != null && q > 1 && amt != null && Number.isFinite(q) && Number.isFinite(amt);
                  return (
                    <div key={i}>
                      <div className="grid grid-cols-[minmax(0,1fr)_3.25rem_6rem_1.75rem] gap-1.5 items-center">
                        <Field placeholder="Description" value={li.description || ''} onChange={(e) => setLine(i, { description: e.target.value })} disabled={locked} />
                        <Field className="text-center !px-1 font-mono tabular-nums" placeholder="Qty" inputMode="decimal" value={li.quantity != null ? String(li.quantity) : ''} onChange={(e) => setLine(i, { quantity: e.target.value })} disabled={locked} />
                        <Field className={moneyClass} placeholder="$" inputMode="decimal" value={li.amount != null ? String(li.amount) : ''} onChange={(e) => setLine(i, { amount: e.target.value })} disabled={locked} />
                        {!locked && (
                          <Tooltip label="Remove line">
                            <IconButton size="sm" label="Remove line" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>
                              <IconClose />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                      <Reveal show={showEa}>
                        {showEa && (
                          <div className="pt-0.5 ml-1 text-[10px] text-on-surface-variant font-mono tabular-nums">{q} × ${(amt / q).toFixed(2)} each</div>
                        )}
                      </Reveal>
                    </div>
                  );
                })}
                <Reveal show={lines.length === 0}><EmptyState variant="line" dense>No line items.</EmptyState></Reveal>
              </div>
            </div>

            <LabeledField label="Note (optional)"><Field value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. client dinner, John's birthday" disabled={locked} /></LabeledField>

            <div className="!-mb-3 empty:!mb-0 empty:!mt-0">
              <Reveal show={!isForeign && !locked && missingRequired.length > 0}>
                <p className="pb-3 text-[11px] text-amber-400/90 text-right">Still needed before submitting: {missingRequired.join(', ')}</p>
              </Reveal>
            </div>
            {!locked && (
              <div className="flex items-center justify-end gap-3 pt-1">
                {/* Hand-off, at the same weight as Review later: both end this
                    person's involvement, and neither files anything. */}
                <Appear show={canHandOff}>
                  <button type="button" onClick={() => setPickRecipient(true)} disabled={saving}
                          aria-haspopup="dialog"
                          className={buttonClass('quiet', 'md')}>
                    {isMine(receipt) ? 'Not my card' : 'Send to someone else'}
                  </button>
                </Appear>
                <button onClick={handleReviewLater} disabled={saving} className={buttonClass('quiet', 'md')}>Review later</button>
                {!isForeign && (
                  <Tooltip label={missingRequired.length ? `Add ${missingRequired.join(', ')} first` : (mathOff && !ackMath ? 'Confirm the totals first' : '')}>
                    <button onClick={handleSubmit} disabled={saving || submitBlocked}
                            className={buttonClass('filled', 'md')}>
                      {saving ? 'Saving…' : submitted ? 'Re-submit' : 'Submit'}
                    </button>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Manual hand-off — the safety net when auto-detection couldn't read the card.
                Opened by "Not my card" in the action row above. It used to be a gray
                text link down here that people scrolled past, so the receipt stayed in
                the wrong queue until somebody chased it. */}
            <HandOffModal
                open={pickRecipient}
                users={users}
                excludeIds={[receipt.owner_user_id || receipt.uploaded_by]}
                loadFailed={usersFailed}
                loading={!usersLoaded}
                // Close first, THEN ask. The confirm dialog renders inside the app
                // tree at the same z-index as this modal's portal, so a confirm
                // raised from in here would open behind it. Picking the person is
                // the modal's whole job anyway; the confirm is the last gate.
                onSelect={(u: any) => {
                  setPickRecipient(false);
                  handleReassign(u.id, `${u.firstname || ''} ${u.lastname || ''}`.trim() || 'them');
                }}
                onClose={() => setPickRecipient(false)}
              />

            {/* Delete: really gone, row and file. Same visibility rule the void link
                had (owners until Dawn approves, financial after, nobody once the period
                is closed) — what changed is the label and the weight. It used to be a
                gray "Void receipt" text link here, which nobody found and nobody outside
                bookkeeping read as "delete". The endpoint it replaced still exists for
                the bookkeeper's keep-on-record case; this button is not it.
                Kept apart from the rescan/retake pair on purpose: a destructive action
                should not sit shoulder to shoulder with two harmless ones. */}
            {!isForeign && receipt.status !== 'void' &&
              !['exported', 'synced'].includes(receipt.status) &&
              (receipt.status !== 'approved' || canFinancial) && (
              <div className="pt-2 flex justify-end">
                <Tooltip label="Delete this receipt for good">
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className={buttonClass('danger', 'md')}
                  >
                    Delete receipt
                  </button>
                </Tooltip>
              </div>
            )}

            {/* Edit history — collapsed by default, lazy-fetched on first expand. */}
            <div className="pt-2 border-t border-gray-800/60">
              <button type="button" onClick={toggleHistory} aria-expanded={historyOpen}
                className="w-full flex items-center gap-2 text-left py-1.5 text-xs font-medium text-on-surface-variant hover:text-on-surface transition">
                <Chevron open={historyOpen} />
                History
              </button>
              <Reveal show={historyOpen}>
                <div className="pt-1 pb-1">
                <FadeIn key={historyLoading ? 'loading' : historyError ? 'error' : editHistory.length === 0 ? 'none' : 'list'} className="space-y-2.5">
                  {historyLoading ? (
                    <SkeletonGroup label="Loading history" className="space-y-1.5 py-1"><Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-2/3" /></SkeletonGroup>
                  ) : historyError ? (
                    <Alert variant="note" onRetry={loadHistory} retryLabel="Retry" retryTip="Load the history again">Could not load history.</Alert>
                  ) : editHistory.length === 0 ? (
                    <EmptyState variant="line" dense className="py-1">No edits yet.</EmptyState>
                  ) : (
                    editHistory.map((e, i) => (
                      <div key={i} className="text-xs">
                        <div className="text-on-surface-variant">{historyRowText(e)}</div>
                        <div className="text-on-surface-variant mt-0.5">{e.edited_by_name || 'Unknown'} · {formatTimestamp(e.created_at)}</div>
                      </div>
                    ))
                  )}
                </FadeIn>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
        </FadeIn>
      )}
    </PageLayout>
  );
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-on-surface-variant mb-1">{label}</span>
      {children}
    </label>
  );
}
